import {createContext, useReducer, useState, useMemo, useEffect} from "react";
import {CountryRegionData} from "react-country-region-selector";
import {parsePersonQueryParams, parseCheckoutQueryParams} from "../utils/personQueryParams";

export const FormContext = createContext(undefined);

export const FormProvider = ({ children }) => {
    const [checkoutVerification, setCheckoutVerification] = useState(null);
    useEffect(() => {
        if (!checkoutVerification) return;
        const timer = setTimeout(() => setCheckoutVerification(null), Math.max(0, checkoutVerification.expiresAt * 1000 - Date.now()));
        return () => clearTimeout(timer);
    }, [checkoutVerification]);
    const [form, setForm] = useState(undefined);
    const [firstName, setFirstName] = useState(undefined);
    const [lastName, setLastName] = useState(undefined);
    const [zipcode, setZipcode] = useState(undefined);
    const [data, setData] = useState({});
    const venue = window.location.pathname.split("/")[1]

    // Parse person-related query parameters once on mount
    const personQueryParams = useMemo(() => parsePersonQueryParams(), []);

    // Parse checkout-related query parameters (amount, donationName)
    const checkoutQueryParams = useMemo(() => parseCheckoutQueryParams(), []);
    const formReducer = (state, event) => {
        return {
            ...state,
            [event.name]: event.value,
            form: form,
            venue: venue
        }
    }

    const tunlUrl = function() {
        if (form !== undefined && form.testMode) {
            return "https://test-payment.tunl.com/CharlieF2"
        } else if(form !== undefined) {
            return "https://payment.tunl.com/" + form.venue.tunl_username.replace(":emv", "")
        }
    }

    const worldnetUrl = function() {
        if (form !== undefined && form.testMode) {
            return "https://testpayments.worldnettps.com/merchant/paymentpage"
        } else if(form !== undefined) {
            return "https://payments.worldnettps.com/merchant/paymentpage"
        }
    }

    const bridgepayUrl = function() {
        if (form !== undefined && form.testMode) {
            return `https://www.bridgepaynetsecuretest.com/hpplive/paymentpage/${form.venue.bridgepay_hpp_id}`
        } else if(form !== undefined) {
            return `https://www.bridgepaynetsecuretx.com/hpplive/paymentpage/${form.venue.bridgepay_hpp_id}`
        }
    }

    const fetchPayrocHostedFieldsSession = async function(processingTerminalId) {
        try {
            const response = await fetch(process.env.REACT_APP_SUBMIT_URL + "/payroc/hosted-fields-session", {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    processingTerminalId: processingTerminalId,
                    testMode: form?.testMode || false
                })
            });
            const data = await response.json();
            if (data.error) {
                console.error('Failed to fetch Payroc Hosted Fields session:', data.error);
                return null;
            }
            return { sessionToken: data.token, expiresAt: data.expiresAt };
        } catch (error) {
            console.error('Failed to fetch Payroc Hosted Fields session:', error);
            return null;
        }
    }

    const fetchApplePaySession = async function(processingTerminalId, appleDomainId, validationUrl) {
        const response = await fetch(process.env.REACT_APP_SUBMIT_URL + "/payroc/apple-pay-session", {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                processingTerminalId,
                appleDomainId,
                validationUrl,
                testMode: form?.testMode || false
            })
        });
        const data = await response.json();
        if (data.error) throw new Error(data.error);
        return data.startSessionResponse;
    }

    const fetchBridgepayAuthToken = async function() {
        if (!form?.venue?.bridgepay_public_key) return null;

        try {
            const response = await fetch(process.env.REACT_APP_SUBMIT_URL + "/bridgepay/auth-token", {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    publicKey: form.venue.bridgepay_public_key,
                    organizationId: form.venue.bridgepay_organization_id,
                    testMode: form.testMode
                })
            });
            const data = await response.json();
            return data.authToken;
        } catch (error) {
            console.error('Failed to fetch BridgePay auth token:', error);
            return null;
        }
    }

    const paymentUrl = function() {
        if (form?.venue?.paymentProcessor === 'payroc_hosted_fields') {
            return null; // No redirect URL — payment happens in-page via hosted fields
        } else if (form?.venue?.paymentProcessor === 'worldnet') {
            return worldnetUrl();
        } else if (form?.venue?.paymentProcessor === 'bridgepay') {
            return bridgepayUrl();
        }
        return tunlUrl(); // Default to TUNL for backward compatibility
    }

    const generateWorldnetDatetime = function() {
        const now = new Date();
        const year = now.getFullYear();
        const month = String(now.getMonth() + 1).padStart(2, '0');
        const day = String(now.getDate()).padStart(2, '0');
        const hours = String(now.getHours()).padStart(2, '0');
        const minutes = String(now.getMinutes()).padStart(2, '0');
        const seconds = String(now.getSeconds()).padStart(2, '0');
        const milliseconds = String(now.getMilliseconds()).padStart(3, '0');
        return `${day}-${month}-${year}:${hours}:${minutes}:${seconds}:${milliseconds}`;
    }

    const generateShortOrderId = function(custref) {
        // Generate a numeric-only order ID from custref
        // Format: timestamp (10 chars) + numeric hash of custref (8 chars) = 18 chars total
        const timestamp = Math.floor(Date.now() / 1000).toString(); // 10 digits

        // Simple hash function to create numeric hash from custref
        let hash = 0;
        for (let i = 0; i < custref.length; i++) {
            const char = custref.charCodeAt(i);
            hash = ((hash << 5) - hash) + char;
            hash = hash & hash; // Convert to 32-bit integer
        }

        // Convert hash to positive number and ensure it's 8 digits using modulo
        const numericHash = Math.abs(hash) % 100000000; // Ensures max 8 digits
        const shortHash = numericHash.toString().padStart(8, '0'); // Pad with zeros to ensure 8 digits

        const orderId = timestamp + shortHash;
        console.log(`Generated numeric order ID: ${orderId} from custref: ${custref}`);
        return orderId;
    }

    const generateWorldnetHashString = function(terminalId, orderId, currency, amount, datetime, receiptPageUrl, validationUrl, secret, isMultiCurrency = false) {
        let hashString;
        if (isMultiCurrency && currency) {
            // Multi-currency terminal format: TERMINALID:ORDERID:CURRENCY:AMOUNT:DATETIME:RECEIPTPAGEURL:VALIDATIONURL:SECRET
            hashString = `${terminalId}:${orderId}:${currency}:${amount}:${datetime}:${receiptPageUrl}:${validationUrl}:${secret}`;
            console.log('Using MULTI-CURRENCY hash format');
        } else {
            // Single currency terminal format: TERMINALID:ORDERID:AMOUNT:DATETIME:RECEIPTPAGEURL:VALIDATIONURL:SECRET
            hashString = `${terminalId}:${orderId}:${amount}:${datetime}:${receiptPageUrl}:${validationUrl}:${secret}`;
            console.log('Using SINGLE-CURRENCY hash format');
        }

        console.log('Worldnet Hash String Components:', {
            terminalId,
            orderId,
            currency: isMultiCurrency ? currency : 'N/A (single currency)',
            amount,
            datetime,
            receiptPageUrl,
            validationUrl,
            secret: secret ? '***REDACTED***' : 'MISSING'
        });
        console.log('Complete Hash String:', hashString);
        return hashString;
    }

    const generateSHA512Hash = async function(inputString) {
        try {
            console.log('Generating SHA-512 hash for:', inputString);

            // Use Web Crypto API for SHA-512 hash generation
            const encoder = new TextEncoder();
            const data = encoder.encode(inputString);
            const hashBuffer = await crypto.subtle.digest('SHA-512', data);

            // Convert buffer to hex string
            const hashArray = Array.from(new Uint8Array(hashBuffer));
            const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');

            const finalHash = hashHex.toLowerCase(); // Worldnet examples show lowercase hex
            console.log('Generated SHA-512 Hash:', finalHash);

            return finalHash;
        } catch (error) {
            console.error('Hash generation failed:', error);
            throw error;
        }
    }

    // Convert country name to ISO 3166-1 2-character code for Worldnet
    const getCountryISOCode = function(countryName) {
        if (!countryName) return "";

        // Find the country in CountryRegionData
        const countryData = CountryRegionData.find(country => country[0] === countryName);

        if (countryData && countryData[1]) {
            return countryData[1]; // Return the 2-char ISO code
        }

        // Fallback for common cases
        if (countryName === "United States" || countryName === "USA") return "US";
        if (countryName === "United Kingdom") return "GB";

        // If no match found, return the original value (will likely cause error, but helps debug)
        console.warn(`Could not find ISO code for country: ${countryName}`);
        return countryName;
    }

    // Worldnet test mode credentials
    const WORLDNET_TEST_TERMINAL_ID = '6025001';
    const WORLDNET_TEST_TERMINAL_SECRET = 'Damageless2025!Gessel';

    // Helper to get the appropriate terminal ID with fallback chain:
    // test mode -> recurring terminal -> non-recurring terminal -> venue legacy
    const getWorldnetTerminalId = function(isRecurring = false) {
        if (form?.testMode && form?.venue?.paymentProcessor === 'worldnet') {
            return WORLDNET_TEST_TERMINAL_ID;
        }
        if (isRecurring && form?.recurringTerminalId?.terminal_id) {
            return form.recurringTerminalId.terminal_id;
        }
        if (form?.nonRecurringTerminalId?.terminal_id) {
            return form.nonRecurringTerminalId.terminal_id;
        }
        return form?.venue?.worldnet_terminal_id;
    }

    // Helper to get the appropriate terminal secret with fallback chain:
    // test mode -> recurring terminal -> non-recurring terminal -> venue legacy
    const getWorldnetTerminalSecret = function(isRecurring = false) {
        if (form?.testMode && form?.venue?.paymentProcessor === 'worldnet') {
            return WORLDNET_TEST_TERMINAL_SECRET;
        }
        if (isRecurring && form?.recurringTerminalId?.terminal_secret) {
            return form.recurringTerminalId.terminal_secret;
        }
        if (form?.nonRecurringTerminalId?.terminal_secret) {
            return form.nonRecurringTerminalId.terminal_secret;
        }
        return form?.venue?.worldnet_terminal_secret;
    }

    const currentBuyer = function() {
        if (formData["buyersEmail"]) {
            return {
                "name": formData["buyersFirstName"] + " " + formData["buyersLastName"],
                "address": formData["buyersAddress"],
                "city": formData["buyersCity"],
                "region": formData["patronRegion"],
                "country": getCountryISOCode(formData["buyersCountry"]),
                "zip": formData["buyersZip"],
                "email": formData["buyersEmail"]
            }
        } else {
            return {
                "name": formData["patronFirstName"] + " " + formData["patronLastName"],
                "address": formData["patronAddress"],
                "city": formData["patronCity"],
                "region": formData["patronRegion"],
                "country": getCountryISOCode(formData["patronCountry"]),
                "zip": formData["patronZip"],
                "email": formData["patronEmail"]
            }
        }
    }

    const [formData, setFormData] = useReducer(formReducer, {});

    function isDataCollectionFormType() {
        if (form === undefined) {
            console.log("form is undefined")
            return false
        }

        console.log("formType", form.formType)
        return ["DataCollection", "MailingList", "EmailList", "VolunteerList", "Rental", "TheaterRental", "Loyalty", "ClassWaitlist", "MovieWaitlist", "ShowWaitlist", "EventWaitlist", "TicketRefund"].indexOf(form.formType) > -1;
    }


    return (
        <FormContext.Provider
            value={{
                form,
                setForm,
                firstName,
                setFirstName,
                lastName,
                setLastName,
                zipcode,
                setZipcode,
                data,
                setData,
                formData,
                setFormData,
                venue,
                tunlUrl,
                worldnetUrl,
                bridgepayUrl,
                paymentUrl,
                generateWorldnetDatetime,
                generateShortOrderId,
                generateWorldnetHashString,
                generateSHA512Hash,
                currentBuyer,
                isDataCollectionFormType,
                fetchBridgepayAuthToken,
                fetchPayrocHostedFieldsSession,
                fetchApplePaySession,
                getWorldnetTerminalId,
                getWorldnetTerminalSecret,
                personQueryParams,
                checkoutQueryParams,
                checkoutVerification,
                setCheckoutVerification
            }}
        >
            {children}
        </FormContext.Provider>
    );
};
